'use client';

/**
 * Sign in / create account, wired to app/main.py's cookie session.
 *
 * Two things from the generated design are deliberately gone:
 *
 *   - The "Demo Accounts" box. It listed two invented addresses
 *     (priya.nair@archlyy.demo / admin@archlyy.demo) that exist in no user
 *     store, so every visitor who clicked "Use" got a 401.
 *   - "Sign in with GitHub". There is no OAuth route on the backend, and a
 *     button that silently does nothing is worse than no button. The forgot-
 *     password flow, which *does* have a route, takes its place.
 */

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { ApiError } from '@/lib/api';
import api from '@/lib/api';
import { useApp } from '@/lib/store';

type LoginForm = { email: string; password: string };
type RegisterForm = {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
};
type ForgotForm = { email: string };

const AFTER_SIGN_IN = '/dashboard';

function PasswordField({
  label,
  placeholder,
  error,
  registration,
}: {
  label: string;
  placeholder: string;
  error?: string;
  registration: ReturnType<ReturnType<typeof useForm>['register']>;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          className="input-dark w-full pr-10"
          placeholder={placeholder}
          autoComplete={label === 'Password' ? 'current-password' : 'new-password'}
          {...registration}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          aria-label={visible ? 'Hide password' : 'Show password'}
        >
          <Icon name={visible ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
        </button>
      </div>
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}

function LoginTab({ onForgot }: { onForgot: () => void }) {
  const { signIn } = useApp();
  const router = useRouter();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    try {
      /*
       * Trim only -- do NOT lowercase or validate as an email.
       *
       * New registrations use the email as the account id, but accounts
       * predating that are keyed by a bare username (the seeded owner account
       * is "jhashouvik"). user_store.get_user() looks up the key verbatim, so
       * lowercasing would break a mixed-case id and an email pattern would
       * reject a legacy username before the request was even sent. This
       * matches what the existing UI does.
       */
      await signIn(data.email.trim(), data.password);
      toast.success('Signed in');
      router.push(AFTER_SIGN_IN);
    } catch (err) {
      // 403 carries the real reason (awaiting approval / suspended); showing
      // the server's message beats a generic "invalid credentials".
      toast.error(err instanceof ApiError ? err.detail : 'Could not sign in. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label htmlFor="login-id" className="block text-sm font-medium mb-1.5">
          Email or username
        </label>
        <input
          id="login-id"
          type="text"
          className="input-dark w-full"
          placeholder="you@company.com"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          {...register('email', { required: 'Enter your email or username' })}
        />
        {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
      </div>

      <PasswordField
        label="Password"
        placeholder="••••••••"
        error={errors.password?.message}
        registration={register('password', { required: 'Password is required' })}
      />

      <div className="flex items-center justify-end">
        <button type="button" onClick={onForgot} className="text-sm text-primary hover:underline">
          Forgot password?
        </button>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="btn-primary w-full py-2.5 flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          <>
            <Icon name="ArrowPathIcon" size={16} className="animate-spin" /> Signing in…
          </>
        ) : (
          'Sign in to Archlyy'
        )}
      </button>
    </form>
  );
}

function RegisterTab({ onRegistered }: { onRegistered: () => void }) {
  const { signUp } = useApp();
  const router = useRouter();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RegisterForm>();
  const password = watch('password');

  const onSubmit = async (data: RegisterForm) => {
    try {
      const { pending } = await signUp(
        data.email.trim().toLowerCase(),
        data.password,
        data.name.trim()
      );
      if (pending) {
        // Admins can require manual approval; the account exists but has no
        // session yet, so send them back to the sign-in tab, not the app.
        toast.success(
          'Account created — an administrator needs to approve it before you can sign in.'
        );
        onRegistered();
        return;
      }
      toast.success('Welcome to Archlyy');
      router.push(AFTER_SIGN_IN);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : 'Could not create the account.');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1.5">Full name</label>
        <input
          type="text"
          className="input-dark w-full"
          placeholder="Your name"
          autoComplete="name"
          {...register('name', { required: 'Name is required' })}
        />
        {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium mb-1.5">Work email</label>
        <input
          type="email"
          className="input-dark w-full"
          placeholder="you@company.com"
          autoComplete="email"
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
      </div>

      <PasswordField
        label="Create password"
        placeholder="Min 8 characters"
        error={errors.password?.message}
        registration={register('password', {
          required: 'Password is required',
          minLength: { value: 8, message: 'Use at least 8 characters' },
        })}
      />

      <PasswordField
        label="Confirm password"
        placeholder="Re-enter password"
        error={errors.confirmPassword?.message}
        registration={register('confirmPassword', {
          required: 'Please confirm your password',
          validate: (v: string) => v === password || 'Passwords do not match',
        })}
      />

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          className="w-3.5 h-3.5 accent-primary mt-0.5 flex-shrink-0"
          {...register('terms', { required: 'You must agree to continue' })}
        />
        <span className="text-xs text-muted-foreground">
          I agree to the Terms of Service and Privacy Policy
        </span>
      </label>
      {errors.terms && <p className="text-xs text-red-400">{errors.terms.message}</p>}

      <button
        type="submit"
        disabled={isSubmitting}
        className="btn-primary w-full py-2.5 flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          <>
            <Icon name="ArrowPathIcon" size={16} className="animate-spin" /> Creating account…
          </>
        ) : (
          'Create free account'
        )}
      </button>
    </form>
  );
}

function ForgotTab({ onBack }: { onBack: () => void }) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotForm>();
  const [sent, setSent] = useState(false);

  const onSubmit = async (data: ForgotForm) => {
    try {
      await api.auth.forgotPassword(data.email.trim().toLowerCase());
      // The endpoint answers the same way whether or not the address exists,
      // so the UI must not imply the account was found.
      setSent(true);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : 'Could not send the reset email.');
    }
  };

  if (sent) {
    return (
      <div className="text-center py-4">
        <Icon name="EnvelopeIcon" size={28} className="text-primary mx-auto mb-3" />
        <p className="text-sm text-muted-foreground leading-relaxed mb-5">
          If that address has an Archlyy account, a reset link is on its way. The link expires in
          one hour.
        </p>
        <button onClick={onBack} className="btn-secondary w-full py-2.5">
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1.5">Email address</label>
        <input
          type="email"
          className="input-dark w-full"
          placeholder="you@company.com"
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
      </div>
      <button
        type="submit"
        disabled={isSubmitting}
        className="btn-primary w-full py-2.5 flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          <>
            <Icon name="ArrowPathIcon" size={16} className="animate-spin" /> Sending…
          </>
        ) : (
          'Send reset link'
        )}
      </button>
      <button type="button" onClick={onBack} className="btn-ghost w-full text-sm">
        Back to sign in
      </button>
    </form>
  );
}

const BRAND_FEATURES = [
  { icon: 'CpuChipIcon', text: 'AI-guided architecture design', accent: '#a78bfa' },
  { icon: 'CurrencyDollarIcon', text: 'Per-resource cost breakdown', accent: '#4ade80' },
  { icon: 'CodeBracketIcon', text: 'One-click Terraform export', accent: '#fb923c' },
  { icon: 'ShieldCheckIcon', text: 'Well-Architected risk review', accent: '#60a5fa' },
];

/** The row at the foot of the brand panel, in each vendor's own colour. */
const BRAND_CLOUDS = [
  { label: 'AWS', tile: 'tile-aws' },
  { label: 'Azure', tile: 'tile-azure' },
  { label: 'GCP', tile: 'tile-gcp' },
];

export default function AuthPage() {
  const [tab, setTab] = useState<'login' | 'register' | 'forgot'>('login');
  const { user, loading } = useApp();
  const router = useRouter();

  // Someone who already has a session should never see this page.
  useEffect(() => {
    if (!loading && user) router.replace(AFTER_SIGN_IN);
  }, [loading, user, router]);

  const heading =
    tab === 'login'
      ? 'Welcome back'
      : tab === 'register'
        ? 'Get started free'
        : 'Reset your password';
  const subheading =
    tab === 'login'
      ? 'Sign in to your Archlyy workspace'
      : tab === 'register'
        ? 'No credit card required.'
        : 'We will email you a link to set a new password.';

  return (
    /*
     * theme-dark-pin, not a plain div: both panels below paint their own fixed
     * dark gradient, but the CSS tokens they draw text from come from <html>.
     * On the light theme that meant near-black --foreground on a near-black
     * panel -- the "Archlyy" wordmark, the headline's first line, and the
     * feature labels were all invisible (exactly what the screenshot shows).
     * Pinning the dark tokens to this subtree fixes every one of them at once,
     * and is what the marketing page already does for the same reason.
     */
    <div className="min-h-screen flex theme-dark-pin">
      {/* Left brand panel */}
      <div
        className="hidden lg:flex flex-col justify-between w-[480px] flex-shrink-0 p-10"
        style={{ background: 'linear-gradient(160deg, #0f172a 0%, #0a0f1e 60%, #020817 100%)' }}
      >
        <div>
          <div className="flex items-center gap-2 mb-12">
            <AppLogo size={32} />
            <span className="font-bold text-xl">Archlyy</span>
            <span className="text-2xs font-bold px-1.5 py-0.5 rounded border border-primary/40 text-primary">
              v1.0
            </span>
          </div>
          <h2 className="text-3xl font-bold leading-tight mb-4">
            Your cloud architecture,
            <br />
            <span className="text-gradient-blue">designed in minutes</span>
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed mb-8">
            Describe what you&apos;re building. Archlyy generates the diagram, cost estimate, and
            Terraform — validated and ready to deploy.
          </p>
          <div className="space-y-4">
            {BRAND_FEATURES.map((item) => (
              <div key={`brand-feat-${item.text}`} className="flex items-center gap-3">
                {/* The plate, its outline, and the glyph all derive from one
                    accent, so a feature's colour cannot half-apply. */}
                <div
                  className="tile-icon w-8 h-8"
                  style={{ ['--tile-accent' as string]: item.accent }}
                >
                  <Icon name={item.icon} size={16} />
                </div>
                <span className="text-sm text-slate-300">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {BRAND_CLOUDS.map((cloud) => (
            <span key={`brand-cloud-${cloud.label}`} className={`chip-cloud ${cloud.tile}`}>
              {cloud.label}
            </span>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div
        className="flex-1 flex items-center justify-center p-6 lg:p-10"
        style={{ background: '#070c19' }}
      >
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <AppLogo size={28} />
            <span className="font-bold text-lg">Archlyy</span>
          </div>

          {tab !== 'forgot' && (
            <div className="flex bg-secondary rounded-lg p-1 mb-6">
              <button
                onClick={() => setTab('login')}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-all duration-150 ${
                  tab === 'login'
                    ? 'bg-primary text-white'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Sign in
              </button>
              <button
                onClick={() => setTab('register')}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-all duration-150 ${
                  tab === 'register'
                    ? 'bg-primary text-white'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Create account
              </button>
            </div>
          )}

          <div className="card-dark p-6 rounded-xl">
            <h1 className="text-lg font-semibold mb-1">{heading}</h1>
            <p className="text-sm text-muted-foreground mb-5">{subheading}</p>
            {tab === 'login' && <LoginTab onForgot={() => setTab('forgot')} />}
            {tab === 'register' && <RegisterTab onRegistered={() => setTab('login')} />}
            {tab === 'forgot' && <ForgotTab onBack={() => setTab('login')} />}
          </div>

          {tab !== 'forgot' && (
            <p className="text-center text-xs text-muted-foreground mt-4">
              {tab === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <button
                onClick={() => setTab(tab === 'login' ? 'register' : 'login')}
                className="text-primary hover:underline"
              >
                {tab === 'login' ? 'Sign up free' : 'Sign in'}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
